import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Wrench, CheckCircle, Lock, RefreshCw, Filter, Plus, AlertCircle } from 'lucide-react';

// ---- helpers ----------------------------------------------------------------

const SOURCE_LABELS = {
  builtin:   { label: 'Built-in',  bg: 'bg-gray-100',   text: 'text-gray-700' },
  platform:  { label: 'Platform',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  org:       { label: 'Org',       bg: 'bg-indigo-100', text: 'text-indigo-700' },
};

const TYPE_LABELS = {
  function:  { label: 'Function',  bg: 'bg-purple-100', text: 'text-purple-700' },
  connector: { label: 'Connector', bg: 'bg-teal-100',   text: 'text-teal-700' },
};

function Badge({ map, value }) {
  const cfg = map[value] ?? { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function SuccessRate({ rate }) {
  if (rate === null || rate === undefined) return <span className="text-gray-400 text-sm">—</span>;
  const pct = Math.round(rate * 100);
  const color = pct >= 90 ? 'text-green-600' : pct >= 70 ? 'text-yellow-600' : 'text-red-500';
  return <span className={`text-sm font-medium ${color}`}>{pct}%</span>;
}

// ---- component --------------------------------------------------------------

export default function ToolsCatalogPage() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterSource, setFilterSource] = useState('');
  const [filterType, setFilterType]   = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  // Tier info (PAYG can't create tools)
  const [tier, setTier] = useState(null);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filterSource) params.source = filterSource;
      if (filterType)   params.tool_type = filterType;
      if (verifiedOnly) params.verified_only = true;
      const res = await axios.get('/api/tools/catalog', { params });
      setTools(res.data.tools || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Authentication required.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load tools.');
      }
    } finally {
      setLoading(false);
    }
  }, [filterSource, filterType, verifiedOnly]);

  useEffect(() => { fetchTools(); }, [fetchTools]);

  useEffect(() => {
    axios.get('/api/v1/org/billing')
      .then(r => setTier(r.data?.subscription?.tier || 'PAYG'))
      .catch(() => setTier('PAYG'));
  }, []);

  const canCreate = tier && tier !== 'PAYG';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header row */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-blue-600" /> Tools Catalog
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Built-in, platform, and org-generated tools available to your workflows
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchTools}
            disabled={loading}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {canCreate ? (
            <button
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
              onClick={() => alert('Tool creation wizard — coming soon')}
            >
              <Plus className="w-4 h-4" /> Create Tool
            </button>
          ) : (
            <div className="flex items-center gap-1.5 bg-gray-100 text-gray-400 text-sm font-medium px-4 py-1.5 rounded-lg cursor-not-allowed select-none" title="Upgrade to PRO or TEAM to create tools">
              <Lock className="w-4 h-4" /> Create Tool
            </div>
          )}
        </div>
      </div>

      {/* PAYG upgrade prompt */}
      {tier === 'PAYG' && (
        <div className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <span>
            Tool creation is available on <strong>PRO</strong> and above. You can use built-in and platform tools on any plan.
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5 bg-white border border-gray-200 rounded-lg p-3">
        <Filter className="w-4 h-4 text-gray-400" />

        <select
          value={filterSource}
          onChange={e => setFilterSource(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All sources</option>
          <option value="builtin">Built-in</option>
          <option value="platform">Platform</option>
          <option value="org">Org</option>
        </select>

        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All types</option>
          <option value="function">Function</option>
          <option value="connector">Connector</option>
        </select>

        <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={verifiedOnly}
            onChange={e => setVerifiedOnly(e.target.checked)}
            className="rounded border-gray-300"
          />
          Verified only
        </label>

        <span className="ml-auto text-xs text-gray-400">{tools.length} tool{tools.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 w-1/3">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Verified</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Locked</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Uses</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Success</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin inline-block mr-2" />
                    Loading tools…
                  </td>
                </tr>
              )}
              {!loading && tools.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    No tools match the current filters.
                  </td>
                </tr>
              )}
              {!loading && tools.map((tool, i) => (
                <tr key={tool.slug ?? tool.name ?? i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{tool.name}</div>
                    {tool.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{tool.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge map={TYPE_LABELS} value={tool.tool_type || 'function'} />
                  </td>
                  <td className="px-4 py-3">
                    <Badge map={SOURCE_LABELS} value={tool.source || 'builtin'} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {tool.is_verified
                      ? <CheckCircle className="w-4 h-4 text-green-500 inline-block" />
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {tool.is_locked
                      ? <Lock className="w-4 h-4 text-red-400 inline-block" />
                      : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {tool.usage_count != null ? tool.usage_count.toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <SuccessRate rate={tool.success_rate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
